import { useState, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import { CLINIC } from "@/lib/api";
import { Menu, X, Phone, ChevronDown, ChevronRight } from "lucide-react";

const SERVICES = [
  { to: "/servicii", label: "Toate serviciile" },
  { to: "/implant-dentar-bacau", label: "Implanturi dentare" },
  { to: "/dinti-ficsi-pe-implanturi-bacau/", label: "All-on-4 și All-on-6" },
  { to: "/coroane-dentare-cerec", label: "Coroane CEREC" },
  { to: "/protetica-dentara", label: "Protetică dentară" },
  { to: "/radiologie-digitala-cbct", label: "Radiologie și CBCT" },
  { to: "/endodontie-microscop", label: "Endodonție la microscop" },
  { to: "/parodontologie-laser", label: "Parodontologie și laser" },
  { to: "/igienizare-dentara-ems", label: "Igienizare EMS" },
  { to: "/ortodontie", label: "Ortodonție" },
  { to: "/urgente-stomatologice", label: "Urgențe stomatologice" },
  { to: "/modjaw-4d-bacau", label: "MODJAW 4D" },
  { to: "/diagnostic-digital-integrat", label: "Diagnostic digital integrat" },
  { to: "/albire-dentara-laser-biolase", label: "Albire dentară cu laser Biolase" },
  { to: "/tratament-bruxism-uzura-dentara/", label: "Bruxism și uzură dentară" },
];

// Mobile top-level order (13 items). Servicii is a toggle, marked with `submenu: true`.
const MOBILE_NAV = [
  { to: "/", label: "Acasă" },
  { to: "/despre-noi", label: "Despre clinică" },
  { to: "/implant-dentar-bacau", label: "Implanturi" },
  { to: "/dinti-ficsi-pe-implanturi-bacau/", label: "Dinți ficși – All-on-4 / All-on-6" },
  { to: "/coroane-dentare-cerec", label: "Coroană într-o zi" },
  { submenu: true, label: "Servicii" },
  { to: "/cazuri-tratate", label: "Cazuri tratate" },
  { to: "/echipa-medicala", label: "Echipa" },
  { to: "/pacienti-din-strainatate", label: "Pacienți din străinătate" },
  { to: "/intrebari-frecvente", label: "Întrebări frecvente" },
  { to: "/blog", label: "Articole" },
  { to: "/tarife", label: "Tarife" },
  { to: "/contact", label: "Contact" },
];

// Desktop nav keeps the current concise structure.
const DESKTOP_NAV_LEFT = [
  { to: "/", label: "Acasă" },
  { to: "/despre-noi", label: "Despre clinică" },
  { to: "/implant-dentar-bacau", label: "Implanturi" },
  { to: "/dinti-ficsi-pe-implanturi-bacau/", label: "Dinți ficși (All-on-4/6)", nowrap: true },
  { to: "/coroane-dentare-cerec", label: "Coroană într-o zi" },
];
const DESKTOP_NAV_RIGHT = [
  { to: "/cazuri-tratate", label: "Cazuri tratate" },
  { to: "/echipa-medicala", label: "Echipa" },
  { to: "/pacienti-din-strainatate", label: "Pacienți din străinătate" },
  { to: "/intrebari-frecvente", label: "Întrebări frecvente" },
  { to: "/blog", label: "Articole" },
  { to: "/tarife", label: "Tarife" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [srvOpenDesktop, setSrvOpenDesktop] = useState(false);
  const [srvOpenMobile, setSrvOpenMobile] = useState(false); // closed by default

  // Honour a menu tap made on the early-HTML shell before React was ready.
  useEffect(() => {
    if (window.__implantShellMenuTap) { window.__implantShellMenuTap = false; setOpen(true); }
  }, []);

  // Body scroll lock while mobile menu is open
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  const closeMenu = () => { setOpen(false); setSrvOpenMobile(false); };

  return (
    <>
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/90 border-b border-slate-200" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="logo-link" aria-label="Denti Service Bacău — pagina de start">
          <picture>
            <source type="image/webp" srcSet="/logo-dentiservice-80.webp" />
            <img
              src="/logo-dentiservice-80.png"
              alt="Denti Service Bacău"
              width="317"
              height="80"
              fetchpriority="high"
              decoding="async"
              className="h-9 md:h-10 w-auto shrink-0 select-none"
              draggable="false"
            />
          </picture>
          <div className="leading-tight sr-only">
            <div className="font-display font-extrabold text-[#0F2C59]">Denti Service</div>
            <div className="text-[10px] tracking-widest text-slate-500 uppercase">Bacău · din 1992</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1 text-sm">
          {DESKTOP_NAV_LEFT.map((n) => (
            <NavLink key={n.to} to={n.to} className={({ isActive }) => `px-3 py-2 rounded-full font-medium transition-colors ${n.nowrap ? 'whitespace-nowrap' : ''} ${isActive ? 'bg-slate-100 text-[#0F2C59]' : 'text-slate-700 hover:text-[#0F2C59]'}`}>{n.label}</NavLink>
          ))}
          <div className="relative" onMouseEnter={() => setSrvOpenDesktop(true)} onMouseLeave={() => setSrvOpenDesktop(false)}>
            <button className="px-3 py-2 rounded-full font-medium text-slate-700 hover:text-[#0F2C59] inline-flex items-center gap-1" data-testid="nav-services-toggle">
              Servicii <ChevronDown className="w-4 h-4" />
            </button>
            {srvOpenDesktop && (
              <div className="absolute top-full left-0 pt-2 w-72">
                <div className="bg-white border border-slate-200 rounded-2xl shadow-lg p-2">
                  {SERVICES.map((s) => (
                    <Link key={s.to} to={s.to} className="block px-3 py-2 rounded-lg text-sm text-slate-700 hover:bg-slate-100 hover:text-[#0F2C59]">{s.label}</Link>
                  ))}
                </div>
              </div>
            )}
          </div>
          {DESKTOP_NAV_RIGHT.map((n) => (
            <NavLink key={n.to} to={n.to} className={({ isActive }) => `px-3 py-2 rounded-full font-medium transition-colors ${isActive ? 'bg-slate-100 text-[#0F2C59]' : 'text-slate-700 hover:text-[#0F2C59]'}`}>{n.label}</NavLink>
          ))}
        </nav>

        <a href={CLINIC.phoneTel} className="hidden md:inline-flex items-center gap-2 bg-[#0F2C59] text-white rounded-full px-4 py-2 text-sm font-semibold hover:bg-[#163d7a] transition-colors shrink-0 whitespace-nowrap" data-testid="header-call-btn">
          <Phone className="w-4 h-4" /> {CLINIC.phone}
        </a>

        <button className="lg:hidden p-2 text-[#0F2C59]" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label={open ? "Închide meniul" : "Deschide meniul"}>
          {open ? <X /> : <Menu />}
        </button>
      </div>
    </header>

      {open && (
        <div className="lg:hidden fixed left-0 right-0 top-16 bottom-0 z-[70] bg-white border-t border-slate-200 flex flex-col" data-testid="mobile-menu" role="dialog" aria-modal="true" aria-label="Meniu principal">
          <div className="flex-1 overflow-y-auto overscroll-contain px-5 py-2">
            {MOBILE_NAV.map((n) => {
              if (n.submenu) {
                return (
                  <div key="servicii-submenu" className="border-b border-slate-100">
                    <button
                      onClick={() => setSrvOpenMobile((v) => !v)}
                      className="w-full min-h-[44px] flex items-center justify-between py-3 text-slate-800 font-medium text-base"
                      data-testid="mobile-services-toggle"
                      aria-expanded={srvOpenMobile}
                    >
                      <span>{n.label}</span>
                      <ChevronRight className={`w-5 h-5 text-slate-500 transition-transform ${srvOpenMobile ? "rotate-90" : ""}`} />
                    </button>
                    {srvOpenMobile && (
                      <div className="pb-2 pl-4 border-l-2 border-[#14B8A6]/40 ml-1" data-testid="mobile-services-list">
                        {SERVICES.map((s) => (
                          <Link
                            key={s.to}
                            to={s.to}
                            onClick={closeMenu}
                            className="block min-h-[44px] py-2.5 pl-3 text-slate-700 text-[15px] hover:text-[#0F2C59]"
                            data-testid={`mobile-service-${s.to}`}
                          >
                            {s.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "/"}
                  onClick={closeMenu}
                  className={({ isActive }) => `block min-h-[44px] py-3 border-b border-slate-100 font-medium text-base ${isActive ? "text-[#0F2C59]" : "text-slate-800"}`}
                  data-testid={`mobile-nav-${n.to}`}
                >
                  {n.label}
                </NavLink>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}
